/* ============================================
   useWikiAnnotations — Text Annotation Hook
   Highlights & notes on wiki article content.
   Uses supabase.from() directly (documented exception)
   ============================================ */

import { useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'

/* ── Types ── */
export interface WikiAnotacion {
  id: string
  articulo_id: string
  user_id: string
  texto_seleccionado: string
  selector_start: string
  offset_start: number
  selector_end: string
  offset_end: number
  nota: string | null
  color: string
  created_at: string
  updated_at: string
}

export interface WikiAnotacionInput {
  articulo_id: string
  texto_seleccionado: string
  selector_start: string
  offset_start: number
  selector_end: string
  offset_end: number
  nota?: string | null
  color?: string
}

const DEFAULT_COLOR = '#fbbf24'

export function useWikiAnnotations() {
  const { user } = useAuth()
  const [anotaciones, setAnotaciones] = useState<WikiAnotacion[]>([])
  const [loading, setLoading] = useState(false)

  /* ── Load annotations for an article ── */
  const loadAnotaciones = useCallback(async (articuloId: string) => {
    if (!user) return
    setLoading(true)
    try {
      const { data, error } = await (supabase
        .from('wiki_anotaciones') as any)
        .select('*')
        .eq('articulo_id', articuloId)
        .eq('user_id', user.id)
        .order('created_at', { ascending: true })

      if (error) throw error
      setAnotaciones((data || []) as WikiAnotacion[])
    } catch (err) {
      console.error('Error loading wiki anotaciones:', err)
    }
    setLoading(false)
  }, [user])

  /* ── Create annotation ── */
  const crearAnotacion = useCallback(async (input: WikiAnotacionInput): Promise<WikiAnotacion | null> => {
    if (!user) return null
    try {
      const { data, error } = await (supabase
        .from('wiki_anotaciones') as any)
        .insert({
          ...input,
          user_id: user.id,
          color: input.color || DEFAULT_COLOR,
          nota: input.nota || null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .select('*')
        .single()

      if (error) throw error
      const anotacion = data as WikiAnotacion
      setAnotaciones(prev => [...prev, anotacion])
      return anotacion
    } catch (err) {
      console.error('Error creating wiki anotacion:', err)
      return null
    }
  }, [user])

  /* ── Update note text ── */
  const actualizarNota = useCallback(async (id: string, nota: string | null): Promise<boolean> => {
    try {
      const { error } = await (supabase
        .from('wiki_anotaciones') as any)
        .update({
          nota,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)

      if (error) throw error
      setAnotaciones(prev =>
        prev.map(a => a.id === id ? { ...a, nota, updated_at: new Date().toISOString() } : a)
      )
      return true
    } catch (err) {
      console.error('Error updating wiki anotacion:', err)
      return false
    }
  }, [])

  /* ── Delete annotation ── */
  const eliminarAnotacion = useCallback(async (id: string): Promise<boolean> => {
    try {
      const { error } = await (supabase
        .from('wiki_anotaciones') as any)
        .delete()
        .eq('id', id)

      if (error) throw error
      setAnotaciones(prev => prev.filter(a => a.id !== id))
      return true
    } catch (err) {
      console.error('Error deleting wiki anotacion:', err)
      return false
    }
  }, [])

  return {
    anotaciones,
    loading,
    loadAnotaciones,
    crearAnotacion,
    actualizarNota,
    eliminarAnotacion,
  }
}
